import { Component, OnInit, Input, EventEmitter, Output, OnDestroy } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ValidatorFn } from '@angular/forms';
import { FormFields } from '../../_models/forms/form-fields.interface';
import { Message, MessageService, PrimeIcons } from 'primeng/api';

@Component({
  selector: 'share-form',
  templateUrl: './share-form.component.html',
  styleUrls: ['./share-form.component.scss']
})
export class ShareFormComponent implements OnInit, OnDestroy {
  // inputs and outputs
  // ------------------

  @Input() formFields: FormFields[] = [];
  @Input() formSubmitAction: { label: string; icon: string; styleClass: string; enabled: boolean } =
    {
      label: 'Submit',
      icon: PrimeIcons.CHECK,
      styleClass: 'p-button-primary',
      enabled: true
    }; // form submit button text
  @Input() formActions: { label: string; icon: string; action: string; styleClass: string }[] = []; // form actions to be displayed
  @Input() globalFormValidators: ValidatorFn[] = [];

  @Output() formSubmitted = new EventEmitter<any>();
  @Output() actionTriggered = new EventEmitter<string>(); // Emit action to parent

  @Output() fileSubmitted = new EventEmitter<any>(); // Emit file to parent

  // local variables
  // ---------------

  submitted = false;
  loading = false;
  errorMessages: Message[] = []; // error messages to be displayed when the field is invalid
  form: FormGroup = new FormGroup({}); // generate form dynamically from formFields

  constructor(private fb: FormBuilder) {}

  get f() {
    return this.form.controls;
  }

  ngOnInit(): void {
    // populate form fields dynamically
    this.formFields.forEach((field) => {
      const control = this.fb.control(
        field.value || '',
        Object.values(field.validators || {})
          .map((validator) => {
            if (validator.required) {
              return Validators.required;
            }
            if (validator.minLength) {
              return Validators.minLength(validator.minLength);
            }
            if (validator.maxLength) {
              return Validators.maxLength(validator.maxLength);
            }
            if (validator.pattern) {
              return Validators.pattern(validator.pattern);
            }
            if (validator.email) {
              return Validators.email;
            }
            // Add other validators as needed
            return null; // Return null for unsupported validators
          })
          .filter((v): v is ValidatorFn => v !== null) // Filter out null values
      );
      this.form.addControl(field.name, control);
    });

    this.form.setValidators(this.globalFormValidators); // Set global validators

    this.form.valueChanges.subscribe((value) => {
      // do something here
      this.errorMessages = []; // Clear error messages on value change
    });
  }

  addErrorMessage(severity: string, summary: string, detail: string) {
    const key = 'shareform';
    this.errorMessages.push({
      key,
      severity,
      summary,
      detail,
      closable: true
    });
  }

  public patchValues(values: { [key: string]: any }): void {
    console.log('patchValues', values);
    this.form.patchValue(values);
  }

  onAction(action: string) {
    this.actionTriggered.emit(action); // Emit action to parent
    // todo: handle action could send action to parent component with data as well
  }

  onFileSelect(event: any) {
    const file = event.files[0];
    this.form.patchValue({
      file_upload: file
    });

    const formData = new FormData();
    formData.append('file', file); // Append file to form data

    this.fileSubmitted.emit({
      file: this.form.get('file_upload')?.value,
      formData: formData
    }); // Emit form data to parent

    this.form.get('file_upload')?.markAsDirty(); // Mark file_upload as dirty
    this.form.get('file_upload')?.markAsTouched(); // Mark file_upload as touched
  }

  onSubmit() {
    this.submitted = true;
    this.loading = true;

    console.log('share form values', this.form.value);
    console.log('share form validity', this.form.valid);

    if (this.form.valid) {
      this.loading = false;
      this.formSubmitted.emit(this.form.value);
    } else {
      this.loading = false;
      this.form.markAllAsTouched(); // Mark all controls as touched to

      Object.keys(this.form.controls).forEach((key) => {
        this.form.controls[key].markAsDirty();
      });

      console.warn('share form is invalid');

      this.errorMessages.push({
        key: 'shareform',
        severity: 'error',
        summary: 'Invalid Form',
        detail: 'Please fill in all required fields correctly.',
        closable: true
      });
    }
  }

  ngOnDestroy() {
    // Clean up subscriptions if any
    // this.form.valueChanges.unsubscribe();
  }
}
